import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';

const FLOWLEDGER_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";
const SIMVAULT_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/xz6y59de_77FA3254-428F-4DC1-8494-71315946C436.png";

// TrustBridge TB Logo Component
const TBLogo = () => (
  <div className="w-10 h-10 rounded-xl bg-[#0080FF] flex items-center justify-center shadow-sm">
    <span className="text-white font-bold text-sm">TB</span>
  </div>
);

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // Check if on dark background page
  const isDarkPage = location.pathname === '/simvault';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProductsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Products', 
      children: [
        { name: 'FlowLedger', path: '/flowledger', description: 'Transaction tracking platform' },
        { name: 'SIMVault', path: '/simvault', description: 'Secure SIM hosting' },
      ]
    },
    { name: 'Pricing', path: '/pricing' },
    { name: 'FAQ', path: '/faq' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass border-b border-slate-100 shadow-sm' 
          : isDarkPage ? 'bg-transparent' : 'bg-transparent'
      }`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <TBLogo />
            <span className={`font-semibold text-xl hidden sm:block ${
              isDarkPage && !isScrolled ? 'text-white' : 'text-[#003C83]'
            }`}>
              TrustBridge
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.children ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIsProductsOpen(true)}
                  onMouseLeave={() => setIsProductsOpen(false)}
                >
                  <button 
                    className={`flex items-center gap-1 font-medium transition-colors ${
                      isDarkPage && !isScrolled 
                        ? 'text-slate-200 hover:text-white' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="products-dropdown-trigger"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isProductsOpen && (
                    <div 
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-100 py-2 animate-fade-in"
                      data-testid="products-dropdown-menu"
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block px-4 py-3 hover:bg-slate-50 transition-colors"
                          data-testid={`nav-link-${child.name.toLowerCase()}`}
                        >
                          <div className="font-medium text-slate-900">{child.name}</div>
                          <div className="text-sm text-slate-500">{child.description}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors ${
                    isActive(link.path) 
                      ? isDarkPage && !isScrolled ? 'text-white' : 'text-slate-900'
                      : isDarkPage && !isScrolled ? 'text-slate-200 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${
                isDarkPage && !isScrolled 
                  ? 'text-slate-200 hover:text-white hover:bg-white/10' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
              data-testid="dark-mode-toggle"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/contact">
              <Button 
                variant="ghost" 
                className={`${isDarkPage && !isScrolled ? 'text-slate-200 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="contact-nav-btn"
              >
                Contact Us
              </Button>
            </Link>
            <Link to="/contact?type=demo">
              <Button 
                className="btn-accent"
                data-testid="request-demo-nav-btn"
              >
                Request Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isDarkPage && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-slate-100'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isDarkPage && !isScrolled ? 'text-white' : 'text-slate-900'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isDarkPage && !isScrolled ? 'text-white' : 'text-slate-900'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 top-20 bg-white z-40 animate-fade-in"
          data-testid="mobile-menu"
        >
          <div className="container-main py-6">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.children ? (
                  <div key={link.name} className="py-2">
                    <div className="font-medium text-slate-400 text-sm uppercase tracking-wider mb-2">
                      {link.name}
                    </div>
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`mobile-nav-${child.name.toLowerCase()}`}
                      >
                        <div className="font-medium text-slate-900">{child.name}</div>
                        <div className="text-sm text-slate-500">{child.description}</div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                      isActive(link.path) 
                        ? 'bg-slate-100 text-slate-900' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col gap-3">
                <Link to="/contact" className="w-full">
                  <Button variant="outline" className="w-full" data-testid="mobile-contact-btn">
                    Contact Us
                  </Button>
                </Link>
                <Link to="/contact?type=demo" className="w-full">
                  <Button className="w-full btn-primary" data-testid="mobile-demo-btn">
                    Request Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
